import { useEffect, useRef, useState, useCallback } from "react"

const useScrollAnimation = (options = {}) => {
  const elementRef = useRef(null)
  const [isVisible, setIsVisible] = useState(false)
  const hasTriggered = useRef(false)
  const observerRef = useRef(null)

  // Store options in ref to avoid dependency issues
  const optionsRef = useRef(options)
  optionsRef.current = options

  const handleIntersection = useCallback((entries) => {
    const [entry] = entries
    if (entry.isIntersecting && !hasTriggered.current) {
      hasTriggered.current = true
      setIsVisible(true)
      observerRef.current?.disconnect()
    }
  }, [])

  useEffect(() => {
    if (typeof window === "undefined" || !window.IntersectionObserver) {
      setIsVisible(true)
      return
    }

    const element = elementRef.current
    if (!element) return

    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
      ...optionsRef.current,
    }

    observerRef.current = new IntersectionObserver(handleIntersection, observerOptions)

    observerRef.current.observe(element)

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
    }
  }, [handleIntersection]) // No options dependency needed

  const reset = useCallback(() => {
    hasTriggered.current = false
    setIsVisible(false)

    const element = elementRef.current
    if (element && observerRef.current) {
      observerRef.current.observe(element)
    }
  }, [])

  return [elementRef, isVisible, reset]
}

export default useScrollAnimation
